import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import FinancialIndexCards from "../components/FinancialIndexCards";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { 
  Download, 
  FileText, 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  Users,
  LogOut,
  Loader2,
  Printer,
  Upload,
  Landmark,
  FileDown,
  Trash2,
  ClipboardList
} from "lucide-react";

const REFERRAL_STATUS_META = {
  new: { label: "Novo", className: "bg-slate-100 text-slate-700" },
  assigned: { label: "Atribuído", className: "bg-amber-100 text-amber-800" },
  contacted: { label: "Contactado", className: "bg-purple-100 text-purple-800" },
  in_progress: { label: "Em andamento", className: "bg-sky-100 text-sky-800" },
  closed: { label: "Fechado", className: "bg-emerald-100 text-emerald-800" },
  completed: { label: "Concluído", className: "bg-emerald-100 text-emerald-800" },
  not_closed_contract: { label: "Não fechou contrato", className: "bg-rose-100 text-rose-800" },
};

const REFERRAL_SOURCE_LABELS = {
  client_request: "Solicitada por você",
  manual_admin: "Direcionada pelo administrador",
  auto_critical: "Encaminhada automaticamente",
};

const parseCurrencyInput = (value) => {
  const normalized = String(value || "")
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DiagnosticResults = () => {
  const { diagnosticId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingTxt, setDownloadingTxt] = useState(false);
  const [aiTips, setAiTips] = useState(null);
  const [loadingTips, setLoadingTips] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [showAreaDetail, setShowAreaDetail] = useState(false);
  const [financialAssessment, setFinancialAssessment] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  const [uploadingFinancial, setUploadingFinancial] = useState(false);
  const [reanalyzingFinancial, setReanalyzingFinancial] = useState(false);
  const [financialFiles, setFinancialFiles] = useState([]);
  const [financialNotes, setFinancialNotes] = useState("");
  const [consultantReferrals, setConsultantReferrals] = useState([]);
  const [loadingConsultantReferrals, setLoadingConsultantReferrals] = useState(false);
  const [updatingReferralId, setUpdatingReferralId] = useState(null);

  const fetchResults = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/results`, { withCredentials: true });
      setResults(response.data);
    } catch (error) {
      console.error("Error fetching results:", error);
      toast.error("Erro ao carregar resultados");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [diagnosticId, navigate]);

  const fetchFinancialAssessment = useCallback(async () => {
    setLoadingFinancial(true);
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/financial-assessment`, { withCredentials: true });
      setFinancialAssessment(response.data?.exists ? response.data : null);
    } catch (error) {
      console.error("Error fetching financial assessment:", error);
      setFinancialAssessment(null);
    } finally {
      setLoadingFinancial(false);
    }
  }, [diagnosticId]);

  useEffect(() => {
    fetchResults();
    fetchFinancialAssessment();
  }, [fetchResults, fetchFinancialAssessment]);

  const fetchConsultantReferrals = useCallback(async () => {
    if (user?.role !== "consultant") return;
    setLoadingConsultantReferrals(true);
    try {
      const response = await axios.get(`${API}/referrals`, { withCredentials: true });
      const ownReferrals = (response.data.referrals || []).filter((referral) => referral.diagnostic_id === diagnosticId);
      setConsultantReferrals(ownReferrals);
    } catch (error) {
      console.error("Error fetching consultant referrals:", error);
      setConsultantReferrals([]);
    } finally {
      setLoadingConsultantReferrals(false);
    }
  }, [diagnosticId, user?.role]);

  useEffect(() => {
    fetchConsultantReferrals();
  }, [fetchConsultantReferrals]);

  const updateConsultantReferral = async (referralId, payload) => {
    setUpdatingReferralId(referralId);
    try {
      await axios.put(`${API}/referrals/${referralId}`, payload, { withCredentials: true });
      toast.success("Acompanhamento atualizado");
      fetchConsultantReferrals();
      fetchResults();
    } catch (error) {
      console.error("Error updating consultant referral:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar acompanhamento");
    } finally {
      setUpdatingReferralId(null);
    }
  };

  const fetchAiTips = async () => {
    setLoadingTips(true);
    try {
      const response = await axios.post(`${API}/diagnostics/${diagnosticId}/ai-tips`, {}, { withCredentials: true });
      setAiTips(response.data);
      toast.success("Dicas geradas com sucesso!");
    } catch (error) {
      console.error("Error fetching AI tips:", error);
      toast.error("Erro ao gerar dicas");
    } finally {
      setLoadingTips(false);
    }
  };

  const uploadFinancialAssessment = async () => {
    if (!financialFiles.length) {
      toast.error("Selecione ao menos um arquivo (PDF, XLS ou XLSX)");
      return;
    }

    const formData = new FormData();
    financialFiles.forEach((file) => formData.append("files", file));
    if (financialNotes.trim()) formData.append("notes", financialNotes.trim());

    setUploadingFinancial(true);
    try {
      await axios.post(`${API}/diagnostics/${diagnosticId}/financial-assessment`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Avaliação financeira processada com sucesso");
      setFinancialFiles([]);
      setFinancialNotes("");
      fetchFinancialAssessment();
    } catch (error) {
      console.error("Error uploading financial assessment:", error);
      toast.error(error.response?.data?.detail || "Erro ao processar avaliação financeira");
    } finally {
      setUploadingFinancial(false);
    }
  };

  const downloadFinancialFile = async (fileId, originalName) => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/financial-assessment/files/${fileId}`, {
        withCredentials: true,
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", originalName || `arquivo_financeiro_${fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading financial file:", error);
      toast.error("Erro ao baixar arquivo financeiro");
    }
  };

  const deleteFinancialFile = async (fileId) => {
    try {
      await axios.delete(`${API}/diagnostics/${diagnosticId}/financial-assessment/files/${fileId}`, {
        withCredentials: true
      });
      toast.success("Arquivo removido");
      fetchFinancialAssessment();
    } catch (error) {
      console.error("Error deleting financial file:", error);
      toast.error(error.response?.data?.detail || "Erro ao excluir arquivo");
    }
  };

  const reanalyzeFinancial = async () => {
    setReanalyzingFinancial(true);
    try {
      await axios.post(`${API}/diagnostics/${diagnosticId}/financial-assessment/reanalyze`, {}, {
        withCredentials: true
      });
      toast.success("Reanálise concluída");
      fetchFinancialAssessment();
    } catch (error) {
      console.error("Error reanalyzing financial:", error);
      toast.error(error.response?.data?.detail || "Erro ao reanalisar");
    } finally {
      setReanalyzingFinancial(false);
    }
  };

  const printTipsPdf = () => {
    if (!aiTips) {
      toast.error("Gere as dicas antes de imprimir o plano em PDF");
      return;
    }

    const tipsSections = Object.entries(aiTips.tips || {})
      .map(([area, tips]) => `
        <section class="area-card">
          <div class="area-card-head">
            <h3>${area}</h3>
            <span class="pill">Ação Prioritária</span>
          </div>
          <pre>${tips}</pre>
        </section>
      `)
      .join("");

    const html = `
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <title>Plano de Dicas e Recomendações</title>
          <style>
            body { font-family: "Calibri", "Arial", sans-serif; margin: 0; color: #0f172a; background: #eef2f7; }
            .page { max-width: 850px; margin: 20px auto; background: #fff; border: 1px solid #dbe4ef; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08); }
            .hero { background: linear-gradient(125deg, #001f3f, #0a4b7a); color: #fff; padding: 28px 34px; }
            .hero h1 { margin: 0 0 6px; font-size: 28px; letter-spacing: 0.2px; }
            .hero p { margin: 0; font-size: 14px; opacity: 0.92; }
            .content { padding: 28px 34px 32px; }
            .meta-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 18px; }
            .meta-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
            .meta-item .label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; }
            .meta-item .value { margin-top: 3px; font-size: 14px; color: #0f172a; font-weight: 600; }
            .section-title { margin: 24px 0 10px; font-size: 17px; color: #001f3f; padding-bottom: 6px; border-bottom: 2px solid #e2e8f0; }
            .area-card { margin: 12px 0; padding: 14px; border: 1px solid #dbe4ef; border-left: 5px solid #0a4b7a; border-radius: 8px; background: #fcfdff; }
            .area-card-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 8px; }
            .area-card h3 { margin: 0; font-size: 15px; color: #0f172a; }
            .pill { display: inline-block; font-size: 10px; font-weight: 700; color: #0a4b7a; background: #e0f2fe; border: 1px solid #bae6fd; border-radius: 999px; padding: 3px 8px; text-transform: uppercase; letter-spacing: 0.3px; }
            .area-card pre { margin: 0; white-space: pre-wrap; font-family: "Calibri", "Arial", sans-serif; font-size: 13.5px; line-height: 1.45; color: #334155; }
            .plan { border: 1px solid #dbe4ef; background: #f8fafc; border-radius: 8px; padding: 14px; }
            .plan pre { margin: 0; white-space: pre-wrap; font-family: "Calibri", "Arial", sans-serif; font-size: 13.5px; line-height: 1.5; color: #334155; }
            .note { margin-top: 18px; font-size: 11.5px; color: #64748b; border-top: 1px dashed #cbd5e1; padding-top: 10px; }
            @media print {
              body { background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .page { margin: 0; border: 0; box-shadow: none; max-width: none; }
              .hero { border-radius: 0; }
              .content { padding: 14mm; }
            }
          </style>
        </head>
        <body>
          <main class="page">
            <section class="hero">
              <h1>Plano de Dicas e Recomendações</h1>
              <p>Credita CGE • Diagnóstico Empresarial</p>
            </section>
            <section class="content">
              <div class="meta-grid">
                <div class="meta-item">
                  <div class="label">Empresa</div>
                  <div class="value">${results?.company_name || "N/A"}</div>
                </div>
                <div class="meta-item">
                  <div class="label">Índice Geral</div>
                  <div class="value">${results?.overall_percent?.toFixed?.(0) ?? "0"}%</div>
                </div>
                <div class="meta-item">
                  <div class="label">Emissão</div>
                  <div class="value">${new Date().toLocaleDateString("pt-BR")}</div>
                </div>
              </div>

              <h3 class="section-title">Ações Iniciais por Área Prioritária</h3>
              ${tipsSections || "<p>Sem dados de dicas.</p>"}

              <h3 class="section-title">Plano de Ação Recomendado</h3>
              <div class="plan">
                <pre>${aiTips.action_plan || "Sem plano de ação disponível."}</pre>
              </div>

              <p class="note">
                Este relatório é um direcionamento inicial. Para uma estratégia mais profunda e personalizada por 30 dias, recomendamos o acompanhamento de um consultor da Credita CGE.
              </p>
            </section>
          </main>
          <script>
            window.onload = () => { window.print(); };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Não foi possível abrir a janela de impressão. Verifique o bloqueio de pop-up.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diagnostico_${diagnosticId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF baixado!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erro ao baixar PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadTxt = async () => {
    setDownloadingTxt(true);
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/txt`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diagnostico_${diagnosticId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("TXT baixado!");
    } catch (error) {
      console.error("Error downloading TXT:", error);
      toast.error("Erro ao baixar TXT");
    } finally {
      setDownloadingTxt(false);
    }
  };

  const requestConsultancy = async (area) => {
    try {
      await axios.post(`${API}/referrals`, {
        diagnostic_id: diagnosticId,
        area: area
      }, { withCredentials: true });
      
      toast.success(`Solicitação de consultoria em ${area} enviada!`);
      fetchResults();
    } catch (error) {
      console.error("Error requesting consultancy:", error);
      toast.error(error.response?.data?.detail || "Erro ao solicitar consultoria");
    }
  };

  const getReferralMeta = (status) => REFERRAL_STATUS_META[status] || { label: status || "N/A", className: "bg-slate-100 text-slate-700" };

  const getAreaReferrals = (areaName) => (results?.referrals || []).filter((referral) => referral.area === areaName);

  const formatCurrency = (value) => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const formatCurrencyInputValue = (value) => (value === null || value === undefined || value === "" ? "" : formatCurrency(value));
  const hasAreaReferral = (areaName) => getAreaReferrals(areaName)
    .some((referral) => ["client_request", "manual_admin"].includes(referral.source) && !["closed", "completed", "not_closed_contract"].includes(referral.status));

  const getScoreColor = (score) => {
    if (score >= 7) return "text-emerald-600";
    if (score >= 5) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 7) return "bg-emerald-100 text-emerald-800";
    if (score >= 5) return "bg-amber-100 text-amber-800";
    return "bg-red-100 text-red-800";
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getImprovementFocus = (area) => {
    if (!area) return "Mapeie processos, indicadores e responsabilidades para estruturar a evolução da área.";
    if (area.score < 5) return "Prioridade alta: revisar processos críticos, definir plano de ação semanal e acompanhar indicadores-chave.";
    if (area.score < 7) return "Prioridade média: padronizar rotinas, reforçar controles e executar melhorias contínuas.";
    return "Manter desempenho: consolidar boas práticas e monitorar para evitar regressões.";
  };

  // Custom radar with colors
  const CustomRadarChart = ({ data }) => {
    return (
      <ResponsiveContainer width="100%" height={350}>
        <RadarChart data={data}>
          <PolarGrid stroke="#E2E8F0" />
          <PolarAngleAxis
            dataKey="shortArea"
            tick={{ fill: "#334155", fontSize: 11, fontWeight: 500 }}
          />
          <PolarRadiusAxis 
            angle={30} 
            domain={[0, 10]} 
            tick={{ fill: '#94a3b8', fontSize: 10 }}
          />
          <Radar
            name="Nota"
            dataKey="score"
            stroke="#001F3F"
            fill="#001F3F"
            fillOpacity={0.2}
            strokeWidth={2}
          />
          <Tooltip 
            formatter={(value) => [`${value.toFixed(2)}/10`, 'Nota']}
            labelFormatter={(label, payload) => payload[0]?.payload?.fullArea || label}
          />
        </RadarChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!results) return null;

  // Prepare radar data
  const radarData = results.radar_data?.map(item => ({
    ...item,
    shortArea: item.area.length > 12 ? item.area.substring(0, 12) + "..." : item.area,
    fullArea: item.area
  })) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-navy rounded flex items-center justify-center">
              <span className="text-white font-serif font-bold text-lg">C</span>
            </div>
            <span className="font-serif text-xl font-semibold text-navy">Credita CGE</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-btn"
            >
              {downloadingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              PDF
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={downloadTxt}
              disabled={downloadingTxt}
              data-testid="download-txt-btn"
            >
              {downloadingTxt ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              TXT
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              data-testid="results-logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Link to="/dashboard" className="inline-flex items-center text-slate-600 hover:text-navy mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Dashboard
        </Link>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-serif font-bold text-navy mb-2">
            Resultado do Diagnóstico
          </h1>
          <p className="text-slate-600">{results.company_name}</p>
        </div>

        {/* Overall Score */}
        <div className="grid lg:grid-cols-3 gap-8 mb-10">
          <Card className="lg:col-span-1 border-0 shadow-lg">
            <CardContent className="p-6 sm:p-8 text-center">
              <h2 className="text-sm font-medium text-slate-600 mb-4">Índice Geral de Gestão</h2>
              <div className={`text-6xl font-serif font-bold mb-2 ${getScoreColor(results.overall_percent / 10)}`}>
                {results.overall_percent?.toFixed(0)}%
              </div>
              <Badge className={`text-base px-4 py-1 ${getScoreBgColor(results.overall_score)}`}>
                {results.overall_band}
              </Badge>
              <p className="text-sm text-slate-500 mt-4">
                Nota: {results.overall_score?.toFixed(2)}/10
              </p>
            </CardContent>
          </Card>

          {/* Radar Chart */}
          <Card className="lg:col-span-2 border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Visão Geral por Área</CardTitle>
              <CardDescription>Verde (≥7), Amarelo (5-6.99), Vermelho (&lt;5)</CardDescription>
            </CardHeader>
            <CardContent>
              {radarData?.length ? (
                <CustomRadarChart data={radarData} />
              ) : (
                <div className="rounded-sm bg-slate-50 p-6 text-sm text-slate-600">
                  Não há áreas aplicáveis suficientes para exibir o gráfico deste diagnóstico.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-navy" />
              Consultorias Relacionadas a Este Diagnóstico
            </CardTitle>
            <CardDescription>
              Sinalização das áreas já solicitadas por você ou já direcionadas pela administração.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {results.referrals?.length ? (
              <div className="space-y-3">
                {results.referrals.map((referral) => {
                  const statusMeta = getReferralMeta(referral.status);
                  return (
                    <div key={referral.referral_id} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-navy">{referral.area}</p>
                          <p className="text-xs text-slate-500">{REFERRAL_SOURCE_LABELS[referral.source] || "Consultoria vinculada ao diagnóstico"}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
                          {referral.consultant_name ? (
                            <Badge variant="outline">Consultor: {referral.consultant_name}</Badge>
                          ) : (
                            <Badge variant="outline">Consultor ainda não definido</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Ainda não há solicitação de consultoria ou direcionamento administrativo vinculado a este diagnóstico.
              </p>
            )}
          </CardContent>
        </Card>

        {user?.role === "consultant" && (
          <Card className="border-0 shadow-md mb-10">
            <CardHeader>
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-navy" />
                Acompanhamento da Consultoria
              </CardTitle>
              <CardDescription>
                Atualize o status, registre observações e mantenha os valores da consultoria neste diagnóstico.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingConsultantReferrals ? (
                <p className="text-sm text-slate-500">Carregando encaminhamentos do consultor...</p>
              ) : consultantReferrals.length === 0 ? (
                <p className="text-sm text-slate-500">Você ainda não possui encaminhamento ativo vinculado a este diagnóstico.</p>
              ) : (
                <div className="space-y-4">
                  {consultantReferrals.map((referral) => {
                    const statusMeta = getReferralMeta(referral.status);
                    return (
                      <div key={referral.referral_id} className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="font-semibold text-navy">{referral.area}</p>
                            <p className="text-xs text-slate-500">{REFERRAL_SOURCE_LABELS[referral.source] || "Consultoria vinculada"}</p>
                          </div>
                          <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Status da consultoria</Label>
                            <Select
                              value={referral.status}
                              onValueChange={(value) => updateConsultantReferral(referral.referral_id, { status: value })}
                              disabled={updatingReferralId === referral.referral_id}
                            >
                              <SelectTrigger data-testid={`consultant-result-status-${referral.referral_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(REFERRAL_STATUS_META).map(([value, meta]) => (
                                  <SelectItem key={value} value={value}>{meta.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Valor do contrato</Label>
                            <Input
                              type="text"
                              inputMode="decimal"
                              defaultValue={formatCurrencyInputValue(referral.contract_value)}
                              onFocus={(e) => {
                                e.target.value = referral.contract_value ?? "";
                              }}
                              onBlur={(e) => {
                                const parsedValue = parseCurrencyInput(e.target.value);
                                e.target.value = parsedValue > 0 ? formatCurrency(parsedValue) : "";
                                if (String(parsedValue) !== String(referral.contract_value ?? "")) {
                                  updateConsultantReferral(referral.referral_id, { contract_value: parsedValue });
                                }
                              }}
                              data-testid={`consultant-result-contract-${referral.referral_id}`}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Observações do consultor</Label>
                          <Textarea
                            placeholder="Registre as tratativas, próximos passos e percepção do atendimento..."
                            defaultValue={referral.notes_consultant || ""}
                            onBlur={(e) => {
                              if (e.target.value !== referral.notes_consultant) {
                                updateConsultantReferral(referral.referral_id, { notes_consultant: e.target.value });
                              }
                            }}
                            className="min-h-[110px]"
                            data-testid={`consultant-result-notes-${referral.referral_id}`}
                          />
                        </div>
                        <div className="grid gap-3 rounded-lg bg-slate-50 p-4 md:grid-cols-2">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500">Previsão do consultor</p>
                            <p className="mt-1 text-sm font-semibold text-navy">{formatCurrency(referral.consultant_amount)}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500">Previsão da empresa</p>
                            <p className="mt-1 text-sm font-semibold text-navy">{formatCurrency(referral.company_amount)}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500">Regra atual</p>
                            <p className="mt-1 text-sm text-slate-700">
                              {Number(referral.consultant_split_percent ?? 70).toFixed(0)}% consultor / {Number(referral.company_split_percent ?? 30).toFixed(0)}% empresa
                            </p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500">Situação financeira</p>
                            <p className="mt-1 text-sm text-slate-700">{referral.financial_estado || "Sem avaliação financeira"}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Strengths and Critical Areas */}
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          {/* Strengths */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                Pontos Fortes
              </CardTitle>
              <CardDescription>Áreas com melhor desempenho</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {results.strengths?.slice(0, 3).map((area, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-emerald-50 rounded-sm">
                    <span className="text-sm font-medium text-slate-700">{area.area}</span>
                    <Badge className="bg-emerald-100 text-emerald-800">{area.score?.toFixed(2)}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Critical Areas */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                Áreas Críticas
              </CardTitle>
              <CardDescription>Áreas que precisam de atenção</CardDescription>
            </CardHeader>
            <CardContent>
              {results.critical_areas?.length ? (
                <div className="space-y-3">
                  {results.critical_areas?.slice(0, 3).map((area, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-sm">
                      <div>
                        <span className="text-sm font-medium text-slate-700">{area.area}</span>
                        {!!getAreaReferrals(area.area).length && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {getAreaReferrals(area.area).map((referral) => {
                              const statusMeta = getReferralMeta(referral.status);
                              return (
                                <Badge key={referral.referral_id} className={statusMeta.className}>
                                  {statusMeta.label}
                                  {referral.consultant_name ? ` • ${referral.consultant_name}` : ""}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-800">{area.score?.toFixed(2)}</Badge>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => requestConsultancy(area.area)}
                          disabled={hasAreaReferral(area.area)}
                          data-testid={`request-consultancy-${index}`}
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {hasAreaReferral(area.area) ? "Já vinculada" : "Solicitar consultor"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-sm bg-emerald-50 p-3 text-sm text-emerald-800">
                  Nenhuma área crítica identificada. Só entram aqui áreas com nota abaixo de 7.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* All Areas Scores */}
        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Notas por Área</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(results.areas_scores || {})
                .sort((a, b) => a.score - b.score)
                .map((area) => (
                <div 
                  key={area.area} 
                  className={`p-4 border-l-4 rounded-sm cursor-pointer transition hover:shadow-sm ${
                    area.score >= 7 ? "border-l-emerald-500 bg-emerald-50" :
                    area.score >= 5 ? "border-l-amber-500 bg-amber-50" :
                    "border-l-red-500 bg-red-50"
                  }`}
                  onClick={() => {
                    setSelectedArea(area);
                    setShowAreaDetail(true);
                  }}
                  data-testid={`area-detail-${area.area}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm text-navy">{area.area}</span>
                    <Badge className={getScoreBgColor(area.score)}>
                      {area.score?.toFixed(2)}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500">{area.band}</p>
                  {!!getAreaReferrals(area.area).length && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {getAreaReferrals(area.area).map((referral) => {
                        const statusMeta = getReferralMeta(referral.status);
                        return (
                          <Badge key={referral.referral_id} className={statusMeta.className}>
                            {statusMeta.label}
                            {referral.consultant_name ? ` • ${referral.consultant_name}` : ""}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 w-full"
                    onClick={(event) => {
                      event.stopPropagation();
                      requestConsultancy(area.area);
                    }}
                    disabled={hasAreaReferral(area.area)}
                    data-testid={`request-consultancy-area-${area.area}`}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    {hasAreaReferral(area.area) ? "Consultoria já vinculada" : "Solicitar consultor para esta área"}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-4">Clique em uma área para ver detalhes das lacunas e foco de melhoria.</p>
          </CardContent>
        </Card>

        {/* Financial Assessment */}
        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Landmark className="h-5 w-5 text-navy" />
              Avaliação Financeira Opcional (DRE/Balanço)
            </CardTitle>
            <CardDescription>
              {["client", "admin"].includes(user?.role)
                ? "Envie relatórios em PDF ou Excel para uma análise inicial por IA."
                : "Relatórios financeiros enviados pela empresa e resumo gerado por IA."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {["client", "admin"].includes(user?.role) && (
              <div className="rounded border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Arquivos financeiros (envie DRE, Balanço ou os dois juntos)</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.xls,.xlsx,.xlsm,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setFinancialFiles(Array.from(e.target.files || []))}
                    className="block w-full text-sm text-slate-600 file:mr-3 file:rounded file:border-0 file:bg-navy file:px-3 file:py-1.5 file:text-white hover:file:bg-navy/90"
                  />
                  {financialFiles.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">{financialFiles.length} arquivo(s) selecionado(s)</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    A IA analisa um único arquivo ou combina DRE + Balanço quando ambos forem enviados.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Observações opcionais</label>
                  <textarea
                    value={financialNotes}
                    onChange={(e) => setFinancialNotes(e.target.value)}
                    placeholder="Ex.: queremos foco em margem, caixa e endividamento"
                    className="w-full rounded border border-slate-300 p-2 text-sm min-h-[72px]"
                  />
                </div>
                <Button
                  onClick={uploadFinancialAssessment}
                  disabled={uploadingFinancial}
                  className="bg-navy hover:bg-navy/90"
                  data-testid="upload-financial-assessment-btn"
                >
                  {uploadingFinancial ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Enviar para Avaliação IA
                    </>
                  )}
                </Button>
              </div>
            )}

            {loadingFinancial ? (
              <p className="text-sm text-slate-500">Carregando avaliação financeira...</p>
            ) : !financialAssessment ? (
              <p className="text-sm text-slate-500">Nenhuma avaliação financeira foi enviada para este diagnóstico.</p>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Estado financeiro</p>
                    <Badge
                      className={`mt-2 ${
                        financialAssessment.estado_financeiro === "Saudável"
                          ? "bg-emerald-100 text-emerald-800"
                          : financialAssessment.estado_financeiro === "Atenção"
                          ? "bg-amber-100 text-amber-800"
                          : financialAssessment.estado_financeiro === "Crítico"
                          ? "bg-red-100 text-red-800"
                          : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {financialAssessment.estado_financeiro || "Indeterminado"}
                    </Badge>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Leitura priorizada</p>
                    <p className="mt-2 text-sm font-semibold text-navy">{financialAssessment.tipo_analise || "Financeira"}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Quantidade de arquivos</p>
                    <p className="mt-2 text-sm font-semibold text-navy">{(financialAssessment.files || []).length} arquivo(s)</p>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h4 className="font-medium text-navy mb-2">Visão Executiva</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-line">
                    {financialAssessment.resumo_executivo || financialAssessment.resumo}
                  </p>
                </div>
                {!!financialAssessment.leitura_empresarial?.length && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <h4 className="font-medium text-navy mb-2">O que isso significa para a empresa</h4>
                    <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                      {financialAssessment.leitura_empresarial.map((item, idx) => (
                        <li key={`fin-business-read-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-navy mb-1">Leitura Técnica Resumida</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{financialAssessment.resumo}</p>
                </div>
                <div>
                  <h4 className="font-medium text-navy mb-2">Índices de Liquidez e Estrutura</h4>
                  <FinancialIndexCards items={financialAssessment.indices_financeiros || []} prefix="fin-index-client" />
                </div>
                <div>
                  <h4 className="font-medium text-navy mb-1">Dicas Primárias</h4>
                  <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
                    {(financialAssessment.dicas_primarias || []).map((tip, idx) => (
                      <li key={`tip-fin-${idx}`}>{tip}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded border border-blue-100 bg-blue-50 p-3">
                  <p className="text-sm text-blue-900">{financialAssessment.recomendacao_consultoria}</p>
                </div>
                <div>
                  <h4 className="font-medium text-navy mb-2">Arquivos Enviados</h4>
                  {["client", "admin"].includes(user?.role) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mb-2"
                      onClick={reanalyzeFinancial}
                      disabled={reanalyzingFinancial}
                    >
                      {reanalyzingFinancial ? "Reanalisando..." : "Reanalisar IA"}
                    </Button>
                  )}
                  <div className="space-y-2">
                    {(financialAssessment.files || []).map((file) => (
                      <div key={file.file_id} className="flex items-center justify-between rounded border border-slate-200 bg-white p-2">
                        <div>
                          <p className="text-sm text-slate-700">{file.original_name}</p>
                          <p className="text-xs text-slate-500">{Math.round((file.size_bytes || 0) / 1024)} KB</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => downloadFinancialFile(file.file_id, file.original_name)}>
                            <FileDown className="h-4 w-4 mr-1" />
                            Baixar
                          </Button>
                          {["client", "admin"].includes(user?.role) && (
                            <Button variant="outline" size="sm" onClick={() => deleteFinancialFile(file.file_id)}>
                              <Trash2 className="h-4 w-4 mr-1" />
                              Excluir
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Tips Section */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-gold" />
                  Dicas e Recomendações
                </CardTitle>
                <CardDescription>Sugestões geradas por IA para melhorar sua gestão</CardDescription>
              </div>
              {!aiTips && (
                <Button 
                  onClick={fetchAiTips}
                  disabled={loadingTips}
                  className="bg-navy hover:bg-navy/90"
                  data-testid="generate-tips-btn"
                >
                  {loadingTips ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Lightbulb className="h-4 w-4 mr-2" />
                      Gerar Dicas
                    </>
                  )}
                </Button>
              )}
              {aiTips && (
                <Button
                  variant="outline"
                  onClick={printTipsPdf}
                  data-testid="print-tips-pdf-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Imprimir PDF
                </Button>
              )}
            </div>
          </CardHeader>
          {aiTips && (
            <CardContent>
              <div className="space-y-6">
                {Object.entries(aiTips.tips || {}).map(([area, tips]) => (
                  <div key={area}>
                    <h4 className="font-medium text-navy mb-2">{area}</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-line">{tips}</p>
                  </div>
                ))}
                
                {aiTips.action_plan && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium text-navy mb-2">Plano de Ação Sugerido (30 dias)</h4>
                      <p className="text-sm text-slate-600 whitespace-pre-line">{aiTips.action_plan}</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Disclaimer */}
        <p className="text-center text-sm text-slate-500 mt-8">
          Este é um diagnóstico inicial automatizado. Para uma avaliação completa e personalizada, 
          entre em contato com um consultor Credita CGE.
        </p>

        <Dialog open={showAreaDetail} onOpenChange={setShowAreaDetail}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">{selectedArea?.area || "Detalhes da Área"}</DialogTitle>
            </DialogHeader>
            {selectedArea && (
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={getScoreBgColor(selectedArea.score)}>
                    Nota {selectedArea.score?.toFixed(2)} - {selectedArea.band}
                  </Badge>
                  {getAreaReferrals(selectedArea.area).map((referral) => {
                    const statusMeta = getReferralMeta(referral.status);
                    return (
                      <Badge key={referral.referral_id} className={statusMeta.className}>
                        {REFERRAL_SOURCE_LABELS[referral.source] || statusMeta.label}
                        {referral.consultant_name ? ` • ${referral.consultant_name}` : ""}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-slate-700">
                  <span className="font-medium text-navy">Lacunas identificadas: </span>
                  {selectedArea.narrative || "Não há narrativa detalhada para esta área."}
                </p>
                <p className="text-slate-700">
                  <span className="font-medium text-navy">Cobertura do diagnóstico: </span>
                  {selectedArea.answered_count ?? 0} de {selectedArea.questions_count ?? 0} perguntas respondidas.
                </p>
                <p className="text-slate-700">
                  <span className="font-medium text-navy">Foco recomendado: </span>
                  {getImprovementFocus(selectedArea)}
                </p>
                <Button
                  onClick={() => requestConsultancy(selectedArea.area)}
                  disabled={hasAreaReferral(selectedArea.area)}
                  className="w-full bg-navy hover:bg-navy/90"
                  data-testid="request-consultancy-area-dialog"
                >
                  <Users className="h-4 w-4 mr-2" />
                  {hasAreaReferral(selectedArea.area) ? "Área já com consultoria vinculada" : "Solicitar consultor para esta área"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default DiagnosticResults;
